import React, { useState, useEffect } from "react";
import { message, Modal, Select } from "antd";
import { getApi } from "../../helpers";
import { opsApiCall } from "../../helpers/rapsapOpsAuth";
import { getAdminRoles } from "../../helpers/checklistApi";
import Popup from "../../components/Popup";

// Store managers always get this role; it's never offered for workers.
const STORE_MANAGER_ROLE_ID = 7;

// Paused for now: set to true to show Access Level (read-only) and Roles
// (workers only) when editing Operations staff, and to lock their Type.
const SHOW_OPERATIONS_ACCESS_FIELDS = false;

const ACCESS_LEVEL_LABELS = { worker: "Worker", store_manager: "Store Manager" };

const getAccessLevel = (staff) => staff?.access_level ?? staff?.accessLevel ?? "";

// Role ids may come back as role_ids / roleIds, or as role objects.
const getRoleIds = (staff) => {
  const roles = staff?.role_ids ?? staff?.roleIds ?? staff?.roles ?? [];
  return Array.isArray(roles)
    ? roles.map((role) => Number(role?.id ?? role?.role_id ?? role)).filter(Boolean)
    : [];
};

export default function EditStaffPopup({
  visible = false,
  initialData,
  onSave,
  onCancel
}) {
  const [loading, setLoading] = useState(false);
  const [storeOptions, setStoreOptions] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    staff_name: "",
    staff_mobile: "",
    staff_email: "",
    store_id: "",
    staff_type: "",
    role_ids: [],
    password: "",
    is_active: 1,
  });
  const [errors, setErrors] = useState({});

  // Access level is fixed once the staff is created, so it is read from the
  // record rather than the form; only workers have editable roles.
  const isOperations = SHOW_OPERATIONS_ACCESS_FIELDS && initialData?.type === "operations";
  const accessLevel = getAccessLevel(initialData);
  const isWorker = isOperations && accessLevel === "worker";

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (initialData && visible) {
      setFormData({
        username: initialData.username || "",
        staff_name: initialData.name || "",
        staff_mobile: initialData.mobile || "",
        staff_email: initialData.email || "",
        store_id: initialData.store_id || "",
        staff_type: initialData.type || "",
        role_ids: getRoleIds(initialData),
        password: "",
        is_active: initialData.is_active !== undefined ? initialData.is_active : 1,
      });
      setErrors({});
    }
  }, [initialData, visible]);

  // Roles come from the Operations API; load them once a worker is edited.
  useEffect(() => {
    if (!visible || !isWorker || roleOptions.length) return;
    setLoadingRoles(true);
    getAdminRoles().then((roles) => {
      if (Array.isArray(roles)) {
        setRoleOptions(
          roles.filter((role) => role.isActive !== false && role.id !== STORE_MANAGER_ROLE_ID)
        );
      }
      setLoadingRoles(false);
    });
  }, [visible, isWorker, roleOptions.length]);

  const fetchStores = async () => {
    try {
      setLoadingStores(true);
      const res = await getApi({}, "getStores", "dashboard/");
      if (res.data && res.data.length) {
        setStoreOptions(res.data);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to load stores");
    } finally {
      setLoadingStores(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username || formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!formData.staff_name || formData.staff_name.length < 2) {
      newErrors.staff_name = "Staff name must be at least 2 characters";
    }

    if (!formData.staff_mobile || !/^[0-9]{10}$/.test(formData.staff_mobile)) {
      newErrors.staff_mobile = "Please enter a valid 10-digit mobile number";
    }

    if (formData.staff_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.staff_email)) {
      newErrors.staff_email = "Please enter a valid email";
    }

    if (!formData.store_id) {
      newErrors.store_id = "Please select a store";
    }

    if (!formData.staff_type) {
      newErrors.staff_type = "Please select staff type";
    }

    if (isWorker && !formData.role_ids.length) {
      newErrors.role_ids = "Please select at least one role";
    }

    if (formData.password && formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    Modal.confirm({
      title: "Confirm Update",
      content: "Are you sure you want to update this staff member's details?",
      onOk: async () => {
        setLoading(true);
        try {
          // Build payload - only include fields that are being updated
          const payload = {
            key: "JWT",
            secret: "RAPSAP",
            staff_id: initialData.user_id?.toString(),
          };

          // Only add fields that have values
          if (formData.username) payload.username = formData.username;
          if (formData.staff_name) payload.staff_name = formData.staff_name;
          if (formData.staff_mobile) payload.staff_mobile = formData.staff_mobile;
          if (formData.staff_email) payload.staff_email = formData.staff_email;
          if (formData.store_id) payload.store_id = formData.store_id.toString();
          if (formData.staff_type) payload.staff_type = formData.staff_type;
          if (isWorker) payload.role_ids = formData.role_ids;
          if (formData.password) payload.password = formData.password;

          payload.is_active = formData.is_active;

          console.log("Updating staff with payload:", payload);

          const res = await opsApiCall("/user/updateStaff", payload);

          if (res) {
            message.success("Staff updated successfully!");
            await onSave();
            onCancel();
          }
        } catch (error) {
          console.error("Error updating staff:", error);
          message.error("Failed to update staff details");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleCancel = () => {
    setFormData({
      username: "",
      staff_name: "",
      staff_mobile: "",
      staff_email: "",
      store_id: "",
      staff_type: "",
      role_ids: [],
      password: "",
      is_active: 1,
    });
    setErrors({});
    onCancel();
  };

  return (
    <Popup
      title="Edit Staff"
      visible={visible}
      width="650px"
      saveText="Update Staff"
      loading={loading}
      saveHandler={handleSubmit}
      cancelHandler={handleCancel}
    >
      <div className="form-container">
        <div className="form-group">
          <label className="form-label">Username</label>
          <input
            type="text"
            placeholder="Enter username"
            value={formData.username}
            onChange={(e) => handleInputChange("username", e.target.value)}
            className={`form-input ${errors.username ? "error" : ""}`}
          />
          {errors.username && <span className="error-text">{errors.username}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Staff Name</label>
          <input
            type="text"
            placeholder="Enter staff name"
            value={formData.staff_name}
            onChange={(e) => handleInputChange("staff_name", e.target.value)}
            className={`form-input ${errors.staff_name ? "error" : ""}`}
          />
          {errors.staff_name && <span className="error-text">{errors.staff_name}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Mobile</label>
          <input
            type="text"
            placeholder="Enter mobile number"
            value={formData.staff_mobile}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              if (value.length <= 10) {
                handleInputChange("staff_mobile", value);
              }
            }}
            maxLength={10}
            className={`form-input ${errors.staff_mobile ? "error" : ""}`}
          />
          {errors.staff_mobile && <span className="error-text">{errors.staff_mobile}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            placeholder="Enter email (optional)"
            value={formData.staff_email}
            onChange={(e) => handleInputChange("staff_email", e.target.value)}
            className={`form-input ${errors.staff_email ? "error" : ""}`}
          />
          {errors.staff_email && <span className="error-text">{errors.staff_email}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Store</label>
          <select
            value={formData.store_id}
            onChange={(e) => handleInputChange("store_id", e.target.value)}
            className={`form-select ${errors.store_id ? "error" : ""}`}
            disabled={loadingStores}
          >
            <option value="">Select Store</option>
            {storeOptions.map((store) => (
              <option key={store.store_id} value={store.store_id}>
                {store.name}
              </option>
            ))}
          </select>
          {errors.store_id && <span className="error-text">{errors.store_id}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Staff Type</label>
          <select
            value={formData.staff_type}
            onChange={(e) => handleInputChange("staff_type", e.target.value)}
            className={`form-select ${errors.staff_type ? "error" : ""}`}
            // An Operations staff's access level can't change, so neither can
            // their type; other staff can't become Operations without one.
            disabled={isOperations}
          >
            <option value="">Select Type</option>
            <option value="package">Package</option>
            <option value="delivery">Delivery</option>
            <option value="admin">Admin</option>
            {(!SHOW_OPERATIONS_ACCESS_FIELDS || isOperations) && (
              <option value="operations">Operations</option>
            )}
          </select>
          {errors.staff_type && <span className="error-text">{errors.staff_type}</span>}
        </div>

        {isOperations && (
          <div className="form-group">
            <label className="form-label">Access Level</label>
            <input
              type="text"
              value={ACCESS_LEVEL_LABELS[accessLevel] || accessLevel || "Not set"}
              className="form-input"
              disabled
            />
          </div>
        )}

        {isWorker && (
          <div className="form-group full-width">
            <label className="form-label">Roles</label>
            <Select
              mode="multiple"
              showSearch={false}
              placeholder="Select roles"
              className={`edit-roles-select ${errors.role_ids ? "error" : ""}`}
              value={formData.role_ids}
              onChange={(value) => handleInputChange("role_ids", value)}
              loading={loadingRoles}
              notFoundContent={loadingRoles ? "Loading..." : "No roles available"}
              options={roleOptions.map((role) => ({ value: role.id, label: role.name }))}
            />
            {errors.role_ids && <span className="error-text">{errors.role_ids}</span>}
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Leave blank to keep current password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              className={`form-input ${errors.password ? "error" : ""}`}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              )}
            </button>
          </div>
          {errors.password && <span className="error-text">{errors.password}</span>}
        </div>

        <div className="form-group full-width">
          <label className="form-label">Active Status</label>
          <div className="status-toggle-wrapper">
            <div className="custom-toggle" onClick={() => handleInputChange("is_active", formData.is_active === 1 ? 0 : 1)}>
              <div className={`toggle-track ${formData.is_active === 1 ? 'active' : 'inactive'}`}>
                <div className="toggle-thumb"></div>
              </div>
              <span className={`toggle-text ${formData.is_active === 1 ? 'active' : 'inactive'}`}>
                {formData.is_active === 1 ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
}
